import {
    ChangeDetectorRef,
    Directive,
    EmbeddedViewRef,
    inject,
    Input,
    TemplateRef,
    ViewContainerRef,
    ɵstringify as stringify
} from "@angular/core";
import { UserStore } from "@dcc/vestas-online/data-access";
import { filter } from "rxjs/operators";
import { takeUntilDestroyed, toObservable } from "@angular/core/rxjs-interop";

@Directive({
    standalone: true,
    selector: "[hasAllowedApplications]"
})
export class HasAllowedApplicationsDirective {
    private userStore = inject(UserStore);
    private _vcr = inject(ViewContainerRef);
    private _tpl = inject<TemplateRef<unknown>>(TemplateRef);
    private _cdr = inject(ChangeDetectorRef);

    private _applicationIds: string[] | null = null;
    private _currentApplicationIds: string[] | null = null;
    private readonly _thenTemplateRef: TemplateRef<unknown> | undefined;
    private _elseTemplateRef: TemplateRef<unknown> | null | undefined;
    private _elseViewRef: EmbeddedViewRef<unknown> | null | undefined;
    private _thenViewRef: EmbeddedViewRef<unknown> | null | undefined;

    @Input()
    set hasAllowedApplications(ids: string[]) {
        this._currentApplicationIds = ids;
        this._updateView();
    }

    @Input()
    set hasAllowedApplicationsElse(templateRef: TemplateRef<unknown> | null) {
        assertTemplate("hasAllowedApplicationsElse", templateRef);
        this._elseTemplateRef = templateRef;
        this._elseViewRef = null; // Clear previous if any
        this._updateView();
    }

    constructor() {
        this._thenTemplateRef = this._tpl;

        toObservable(this.userStore.applicationIds)
            .pipe(
                takeUntilDestroyed(),
                filter((val) => !!val)
            )
            .subscribe((applicationIds) => {
                this._applicationIds = applicationIds;
                this._cdr.markForCheck();
                this._updateView();
            });
    }

    private _updateView() {
        if (this.hasAllowedApplication()) {
            if (this._thenTemplateRef) {
                this._vcr.clear();
                this._elseViewRef = null; // Clear else view
                this._thenViewRef = this._vcr.createEmbeddedView(this._thenTemplateRef);
            }
        } else if (this._elseTemplateRef) {
            this._vcr.clear();
            this._thenViewRef = null;
            this._elseViewRef = this._vcr.createEmbeddedView(this._elseTemplateRef);
        } else {
            this._vcr.clear();
            this._thenViewRef = null;
            this._elseViewRef = null;
        }
    }

    private hasAllowedApplication() {
        if (!this._applicationIds || !this._currentApplicationIds) {
            return true;
        }
        return this._applicationIds.some((id) => this._currentApplicationIds?.includes(id));
    }
}

function assertTemplate(property: string, templateRef: TemplateRef<unknown> | null): void {
    const isTemplateRefOrNull = !!(!templateRef || templateRef.createEmbeddedView);
    if (!isTemplateRefOrNull) {
        throw new Error(`${property} must be a TemplateRef, but received '${stringify(templateRef)}'.`);
    }
}
